import React from 'react';
import styled from 'styled-components';
import { Table } from 'rbx';
import gql from 'graphql-tag';
import swal from 'sweetalert';

import { useMutation } from '@apollo/react-hooks';
import { useStoreState } from 'easy-peasy';
import { isNil } from 'lodash';
import { toast } from 'react-toastify';

import { Table as CustomTable, Box } from '../../../components/elements';

import List from './List';
import AddEditForm from './AddEditForm';
import client from '../../../utils/apolloClient';

const Container = styled.div`
  .table-container {
    min-height: 30rem;
  }
`;

const createCmsRoleMutation = gql`
  mutation createCmsRoleMutation($input: CreateCmsRoleInput) {
    create_cms_role(input: $input) {
      assign_roles
      description
      display_order
      name
      role_id
      status
      acls {
        name
        acl_id
      }
      error {
        code
        description
      }
    }
  }
`;

const updateCmsRoleMutation = gql`
  mutation updateCmsRoleMutation($input: UpdateCmsRoleInput) {
    update_cms_role(input: $input) {
      assign_roles
      description
      display_order
      name
      role_id
      status
      acls {
        name
        acl_id
      }
      error {
        code
        description
      }
    }
  }
`;

const deleteCmsRoleMutation = gql`
  mutation deleteCmsRoleMutation($input: DeleteCmsRoleInput) {
    delete_cms_role(input: $input) {
      assign_roles
      description
      display_order
      name
      role_id
      status
      acls {
        name
        acl_id
      }
      error {
        code
        description
      }
    }
  }
`;

const Details = ({ roles, activeModalProp, refetch }) => {
  const { userId } = useStoreState(state => state.auth);

  const {
    selectedRole,
    setSelectedRole,
    isActiveModal,
    setIsActiveModal,
  } = activeModalProp;

  const [createRole, { loading }] = useMutation(createCmsRoleMutation, {
    client: client.clientPrivate,
    onCompleted: ({ create_cms_role }) => {
      if (!isNil(create_cms_role.error)) {
        create_cms_role.error.map(item => toast.error(item.description));
      } else {
        swal('Successfully added role!').then(() => {
          setIsActiveModal(false);
          refetch();
        });
      }
    },
  });

  const [updateRole, { loading: loading2 }] = useMutation(
    updateCmsRoleMutation,
    {
      client: client.clientPrivate,
      onCompleted: ({ update_cms_role }) => {
        if (!isNil(update_cms_role.error)) {
          update_cms_role.error.map(item => toast.error(item.description));
        } else {
          swal('Successfully updated role!').then(() => {
            setIsActiveModal(false);
            refetch();
            setSelectedRole(null);
          });
        }
      },
    },
  );

  const [deleteRole, { loading: isDeleting }] = useMutation(
    deleteCmsRoleMutation,
    {
      client: client.clientPrivate,
      onCompleted: ({ delete_cms_role }) => {
        if (!isNil(delete_cms_role.error)) {
          delete_cms_role.error.map(item => toast.error(item.description));
        } else {
          swal('Successfully deleted role!').then(() => {
            refetch();
            setSelectedRole(null);
          });
        }
      },
    },
  );

  return (
    <React.Fragment>
      <Box>
        <Container>
          <CustomTable>
            <Table.Head>
              <Table.Row>
                <Table.Heading>Name</Table.Heading>
                <Table.Heading>Description</Table.Heading>
                <Table.Heading>Actions</Table.Heading>
              </Table.Row>
            </Table.Head>
            <Table.Body>
              <List
                roles={roles}
                onEdit={value => {
                  setSelectedRole(value);
                  setIsActiveModal(true);
                }}
                onDelete={async value => {
                  deleteRole({
                    variables: {
                      input: {
                        role_id: value?.role_id,
                        user_id: userId,
                      },
                    },
                  });
                }}
                isDeleting={isDeleting}
              />
            </Table.Body>
          </CustomTable>
        </Container>
      </Box>
      {isActiveModal && (
        <AddEditForm
          role={selectedRole}
          loading={loading || loading2}
          isActive={isActiveModal}
          onClose={() => setIsActiveModal(false)}
          onSubmit={values => {
            if (selectedRole) {
              updateRole({
                variables: {
                  input: {
                    role_id: selectedRole?.role_id,
                    user_id: userId,
                    name: values.name,
                    description: values.description,
                    assign_roles: values.assign_roles,
                    acl_ids: values.acls,
                  },
                },
              });
            } else {
              createRole({
                variables: {
                  input: {
                    user_id: userId,
                    name: values.name,
                    description: values.description,
                    assign_roles: values.assign_roles,
                    acl_ids: values.acls,
                  },
                },
              });
            }
          }}
        />
      )}
    </React.Fragment>
  );
};

export default Details;
